<template>
    <section class="c-Narrow-search__add-opt c-Policy-max-range" v-if="hasRange">
        <div class="c-Policy-max-range__heading-row">
            <label class="c-Narrow-search__field-label c-Policy-max-range__heading">{{ heading }}</label>
            <button
                v-if="modalUrl"
                type="button"
                class="o-Information c-Policy-max-range__information"
                :aria-label="heading"
                @click="$emit('show-modal', modalUrl)"
            ></button>
        </div>

        <div class="c-Narrow-search__add-opt-list c-Policy-max-range__content">
            <div class="c-Policy-max-range__slider">
                <span class="c-Policy-max-range__rail"></span>
                <span class="c-Policy-max-range__selection" :style="selectionStyle"></span>
                <span
                    v-for="(option, index) in policyMaximumOptions"
                    :key="option"
                    class="c-Policy-max-range__stop"
                    :class="{
                        'c-Policy-max-range__stop--selected': index >= effectiveMinimumIndex && index <= effectiveMaximumIndex,
                        'c-Policy-max-range__stop--active': index === effectiveMinimumIndex || index === effectiveMaximumIndex
                    }"
                    :style="positionStyle(index)"
                    :title="formatCurrency(option)"
                    aria-hidden="true"
                ></span>
                <span
                    class="c-Policy-max-range__handle-label c-Policy-max-range__handle-label--minimum"
                    :class="{ 'c-Policy-max-range__handle-label--start': effectiveMinimumIndex === 0 }"
                    :style="positionStyle(effectiveMinimumIndex)"
                >{{ formatCurrency(effectiveMinimum) }}</span>
                <span
                    class="c-Policy-max-range__handle-label c-Policy-max-range__handle-label--maximum"
                    :class="{ 'c-Policy-max-range__handle-label--end': effectiveMaximumIndex === lastOptionIndex }"
                    :style="positionStyle(effectiveMaximumIndex)"
                >{{ formatCurrency(effectiveMaximum) }}</span>
                <input
                    :id="minimumSliderId"
                    class="c-Policy-max-range__handle c-Policy-max-range__handle--minimum"
                    type="range"
                    min="0"
                    :max="lastOptionIndex"
                    step="1"
                    :value="effectiveMinimumIndex"
                    :disabled="disabled"
                    :aria-label="minimumAriaLabel"
                    :style="{ zIndex: activeHandle === 'minimum' ? 2 : 1 }"
                    @input="setMinimumFromSlider"
                >
                <input
                    :id="maximumSliderId"
                    class="c-Policy-max-range__handle c-Policy-max-range__handle--maximum"
                    type="range"
                    min="0"
                    :max="lastOptionIndex"
                    step="1"
                    :value="effectiveMaximumIndex"
                    :disabled="disabled"
                    :aria-label="maximumAriaLabel"
                    :style="{ zIndex: activeHandle === 'maximum' ? 2 : 1 }"
                    @input="setMaximumFromSlider"
                >
            </div>

            <!-- Temporarily disabled: Policy Maximum minimum and maximum number inputs. -->
            <!-- <div class="c-Policy-max-range__inputs">
                <label class="c-Policy-max-range__input-group" :for="minimumInputId">
                    <span class="c-Policy-max-range__input-label">{{ minimumLabel }}</span>
                    <span class="c-Policy-max-range__input-wrap">
                        <span class="c-Policy-max-range__currency" aria-hidden="true">$</span>
                        <input
                            :id="minimumInputId"
                            v-model="minimumDraft"
                            class="c-Policy-max-range__number"
                            type="number"
                            inputmode="numeric"
                            autocomplete="off"
                            :min="minLimit"
                            :max="effectiveMaximum"
                            :step="1"
                            :disabled="disabled"
                            @blur="commitMinimum"
                            @keydown.enter.prevent="$event.target.blur()"
                        >
                    </span>
                </label>

                <label class="c-Policy-max-range__input-group" :for="maximumInputId">
                    <span class="c-Policy-max-range__input-label">{{ maximumLabel }}</span>
                    <span class="c-Policy-max-range__input-wrap">
                        <span class="c-Policy-max-range__currency" aria-hidden="true">$</span>
                        <input
                            :id="maximumInputId"
                            v-model="maximumDraft"
                            class="c-Policy-max-range__number"
                            type="number"
                            inputmode="numeric"
                            autocomplete="off"
                            :min="effectiveMinimum"
                            :max="maxLimit"
                            :step="1"
                            :disabled="disabled"
                            @blur="commitMaximum"
                            @keydown.enter.prevent="$event.target.blur()"
                        >
                    </span>
                </label>
            </div> -->
        </div>
    </section>
</template>

<script>
export default {
    props: {
        value: {
            type: Object,
            default: () => ({ min: null, max: null })
        },
        options: {
            type: Array,
            required: true
        },
        heading: {
            type: String,
            required: true
        },
        minimumLabel: {
            type: String,
            required: true
        },
        maximumLabel: {
            type: String,
            required: true
        },
        modalUrl: {
            type: String,
            default: ''
        },
        disabled: {
            type: Boolean,
            default: false
        }
    },
    data() {
        return {
            activeHandle: 'maximum'
        };
    },
    computed: {
        policyMaximumOptions() {
            const values = this.options
                .map(value => this.toNumber(value))
                .filter(value => value !== null && value > 0);

            return [...new Set(values)].sort((a, b) => a - b);
        },
        hasRange() {
            return this.policyMaximumOptions.length > 1;
        },
        lastOptionIndex() {
            return this.policyMaximumOptions.length - 1;
        },
        requestedMinimumIndex() {
            const minimum = this.toNumber(this.value.min);
            if (minimum === null) return 0;

            const index = this.policyMaximumOptions.findIndex(value => value >= minimum);
            return index === -1 ? this.lastOptionIndex : index;
        },
        requestedMaximumIndex() {
            const maximum = this.toNumber(this.value.max);
            if (maximum === null) return this.lastOptionIndex;

            for (let index = this.lastOptionIndex; index >= 0; index--) {
                if (this.policyMaximumOptions[index] <= maximum) return index;
            }
            return 0;
        },
        effectiveMinimumIndex() {
            return Math.min(this.requestedMinimumIndex, this.requestedMaximumIndex);
        },
        effectiveMaximumIndex() {
            return Math.max(this.requestedMinimumIndex, this.requestedMaximumIndex);
        },
        effectiveMinimum() {
            return this.policyMaximumOptions[this.effectiveMinimumIndex];
        },
        effectiveMaximum() {
            return this.policyMaximumOptions[this.effectiveMaximumIndex];
        },
        minimumSliderId() {
            return `policy-maximum-minimum-slider-${this._uid}`;
        },
        maximumSliderId() {
            return `policy-maximum-maximum-slider-${this._uid}`;
        },
        /* minimumInputId() {
            return `policy-maximum-minimum-input-${this._uid}`;
        },
        maximumInputId() {
            return `policy-maximum-maximum-input-${this._uid}`;
        }, */
        minimumAriaLabel() {
            return `${this.minimumLabel} ${this.heading}: ${this.formatCurrency(this.effectiveMinimum)}`;
        },
        maximumAriaLabel() {
            return `${this.maximumLabel} ${this.heading}: ${this.formatCurrency(this.effectiveMaximum)}`;
        },
        selectionStyle() {
            const left = this.positionPercentage(this.effectiveMinimumIndex);
            const right = this.positionPercentage(this.effectiveMaximumIndex);
            return {
                left: `${left}%`,
                width: `${right - left}%`
            };
        }
    },
    /* watch: {
        effectiveMinimum: {
            immediate: true,
            handler(value) {
                this.minimumDraft = String(value);
            }
        },
        effectiveMaximum: {
            immediate: true,
            handler(value) {
                this.maximumDraft = String(value);
            }
        }
    }, */
    methods: {
        toNumber(value) {
            if (value === '' || value === null || typeof value === 'undefined') return null;
            const number = Number(value);
            return Number.isFinite(number) ? number : null;
        },
        positionPercentage(index) {
            if (this.lastOptionIndex <= 0) return 0;
            return (index / this.lastOptionIndex) * 100;
        },
        positionStyle(index) {
            return { left: `${this.positionPercentage(index)}%` };
        },
        formatCurrency(value) {
            return `$${Number(value).toLocaleString('en-US', { maximumFractionDigits: 0 })}`;
        },
        optionAt(index) {
            const safeIndex = Math.min(Math.max(Number(index), 0), this.lastOptionIndex);
            return this.policyMaximumOptions[safeIndex];
        },
        emitRange(minimum, maximum) {
            this.$emit('input', { min: minimum, max: maximum });
        },
        setMinimumFromSlider(event) {
            this.activeHandle = 'minimum';
            const index = Math.min(Math.max(Number(event.target.value), 0), this.effectiveMaximumIndex);
            event.target.value = String(index);
            this.emitRange(this.optionAt(index), this.effectiveMaximum);
        },
        setMaximumFromSlider(event) {
            this.activeHandle = 'maximum';
            const index = Math.min(Math.max(Number(event.target.value), this.effectiveMinimumIndex), this.lastOptionIndex);
            event.target.value = String(index);
            this.emitRange(this.effectiveMinimum, this.optionAt(index));
        },
        /* commitMinimum() {
            this.activeHandle = 'minimum';
            const minimum = Math.round(this.clamp(this.minimumDraft, this.minLimit, this.effectiveMaximum));
            this.minimumDraft = String(minimum);
            this.emitRange(minimum, this.effectiveMaximum);
        },
        commitMaximum() {
            this.activeHandle = 'maximum';
            const enteredMaximum = this.toNumber(this.maximumDraft);
            const maximum = enteredMaximum === null
                ? this.maxLimit
                : Math.round(this.clamp(enteredMaximum, this.effectiveMinimum, this.maxLimit));
            this.maximumDraft = String(maximum);
            this.emitRange(this.effectiveMinimum, maximum);
        } */
    }
};
</script>
